import type { ReactNode } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { Button } from '@gto/ui/button'

import {
  type CouponPlan,
  type CouponSharedFormValues,
  resolveSubmitPlanIds,
} from '@/domain/coupons'

type CouponSubmitButtonProps = {
  plans: CouponPlan[]
  submitting: boolean
  label: ReactNode
  onClick: () => void
  'data-tst'?: string
}

/**
 * Submit button shared by every coupon form. Beyond the pending guard it blocks
 * a scoped selection that resolves to ZERO plans: that would submit
 * `plan_ids: []`, which the backend reads as "all plans" — silently broadening
 * the discount. (`all` legitimately resolves to []; `custom` always carries its
 * verbatim ids, so neither is blocked.) Reads the form via context so the three
 * dialogs share one guard.
 */
export const CouponSubmitButton = ({
  plans,
  submitting,
  label,
  onClick,
  'data-tst': dataTst,
}: CouponSubmitButtonProps) => {
  const { control } = useFormContext<CouponSharedFormValues>()
  const [scopeKind, scopePlanId, scopeTier, scopeProduct, scopeInterval, customPlanIds] = useWatch({
    control,
    name: [
      'scopeKind',
      'scopePlanId',
      'scopeTier',
      'scopeProduct',
      'scopeInterval',
      'customPlanIds',
    ],
  })

  // `null` = not submittable (incomplete or no-match scope). The shared guard
  // keeps the "never broaden" rule identical to every form's submit handler.
  const notSubmittable =
    resolveSubmitPlanIds(
      { scopeKind, scopePlanId, scopeTier, scopeProduct, scopeInterval, customPlanIds },
      plans,
    ) === null

  return (
    <Button
      data-tst={dataTst}
      disabled={submitting || notSubmittable}
      variant="contained"
      onClick={onClick}
    >
      {label}
    </Button>
  )
}
